"use client";

import Link from "next/link";
import { Fragment, useEffect, useRef, useState } from "react";
import { SmartImage } from "@/components/SmartImage";
import { useCart } from "@/hooks/useCart";
import { useWishlist } from "@/hooks/useWishlist";
import { trackAddToCart, trackAddToWishlist } from "@/lib/analytics";
import { getDisplayRating } from "@/lib/reviews";
import { formatTL } from "@/lib/format";
import type { Product } from "@/lib/products";
import { getProductImages } from "@/lib/products";
import { unsplash } from "@/lib/unsplash";
import { getVariants, hasVariants } from "@/lib/product-variants";

/* ------------------------------------------------------------------ */
/* Yardımcılar                                                         */
/* ------------------------------------------------------------------ */

export { formatTL };

function fallbackFor(slug: string): string {
  if (slug.includes("tips") || slug.includes("soft-gel") || slug.includes("nude-french")) return unsplash.nails(800, 800);
  if (slug.includes("builder") || slug.includes("polygel") || slug.includes("hamur-jel") || slug.includes("sculpture")) return unsplash.polishBottle(800, 800);
  if (slug.includes("prep") || slug.includes("diamond") || slug.includes("top-coat") || slug.includes("base") || slug.includes("remover") || slug.includes("glue") || slug.includes("yapistirici")) return unsplash.bottle(800, 800);
  if (slug.includes("krom") || slug.includes("chrome") || slug.includes("cat-eye") || slug.includes("magnet") || slug.includes("inci-tozu") || slug.includes("pigment")) return unsplash.palette(800, 800);
  if (slug.includes("black") || slug.includes("kalici-oje") || slug.includes("uno-")) return unsplash.polish(800, 800);
  return unsplash.cosmetics(800, 800);
}

/** Figma'da pembe rozetli ürünler */
const TIKTOK_FAVORITES = new Set([
  "qnail-soft-gel-tips-oval-medium-550",
  "uno-diamond-shield-top-coat",
  "qnail-cat-eye-magnet-6li",
  "uno-gel-polish-bronz-12ml",
  "qnail-mbqn-tips-glue-7gr",
  "qnail-sculpture-jel-beyaz-02-15gr",
  "qnail-uno-elastik-base-15ml",
  "qnail-uno-magic-remover",
]);

/* ------------------------------------------------------------------ */
/* İkonlar (tasarımdaki outline ikonların erişilebilir karşılıkları)    */
/* ------------------------------------------------------------------ */

function ChevronLeftIcon({ className = "h-6 w-6" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon({ className = "h-6 w-6" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function PlayIcon({ className = "h-7 w-7" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M8 5.5v13l11-6.5z" />
    </svg>
  );
}

function TruckIcon({ className = "h-8 w-8" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M1 3h15v13H1z" />
      <path d="M16 8h4l3 3v5h-7V8z" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

function ShieldIcon({ className = "h-8 w-8" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function GiftIcon({ className = "h-8 w-8" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3" y="8" width="18" height="4" />
      <path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Paylaşılan küçük parçalar                                           */
/* ------------------------------------------------------------------ */

function AccentWord({ children }: { children: React.ReactNode }) {
   return (
     <span className="relative inline-block text-[#9942C8]">
       {children}
       <span aria-hidden="true" className="absolute -bottom-1 left-0 h-1.5 w-full rounded-full bg-[#9942C8]/70" />
     </span>
   );
 }

export function FigmaStars({ slug }: { slug: string }) {
  const { avg: value, count: shown } = getDisplayRating(slug);
  return (
    <span className="inline-flex items-center gap-1" role="img" aria-label={`5 üzerinden ${value} yıldız (${shown} değerlendirme)`}>
      <span className="flex items-center gap-0.5" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, i) => (
          <svg key={i} viewBox="0 0 24 24" className={`h-4 w-4 ${i < Math.round(value) ? "text-Tertiary" : "text-Tertiary/20"}`} fill="currentColor" aria-hidden="true">
            <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
        ))}
      </span>
      <span className="pl-[3px] text-sm text-zinc-500">({shown})</span>
    </span>
  );
}

function FigmaHeart({ product }: { product: Product }) {
  const { has, toggle } = useWishlist();
  const active = has(product.slug);
  return (
    <button
      type="button"
      onClick={() => {
        const adding = !active;
        toggle({ slug: product.slug, name: product.name, price: product.price, image: product.image });
        if (adding) trackAddToWishlist({ id: product.slug, name: product.name, price: product.price });
      }}
      aria-label={active ? `${product.name} ürününü favorilerden çıkar` : `${product.name} ürününü favorilere ekle`}
      aria-pressed={active}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-white/50 bg-white/20 text-Primary-dark shadow backdrop-blur-[10px] transition hover:scale-105 active:scale-95 sm:h-12 sm:w-12"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4 sm:h-6 sm:w-6" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    </button>
  );
}

export function FigmaAddButton({ product, variant = "outline" }: { product: Product; variant?: "outline" | "white" | "fill" }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  if (!product.inStock) {
    return (
      <span
        aria-disabled="true"
        className="inline-flex h-10 w-full cursor-not-allowed items-center justify-center rounded-full bg-Background-light px-4 text-xs font-medium uppercase text-500 outline outline-1 outline-Background-dark sm:h-12 sm:px-6 sm:text-base"
      >
        Stokta Yok
      </span>
    );
  }
  // Varyantlı üründe karttan direkt ekleme yok — PDP'deki seçiciye yönlendir
  if (hasVariants(product.slug)) {
    return (
      <Link
        href={`/urunler/${product.slug}`}
        className={
          variant === "white"
            ? "inline-flex h-10 items-center justify-center rounded-full bg-white px-4 text-xs font-medium uppercase text-Primary transition hover:bg-Background-light active:scale-[0.98] sm:h-12 sm:px-6 sm:text-base"
            : variant === "fill"
              ? "inline-flex h-10 w-full items-center justify-center rounded-full bg-Primary px-4 text-xs font-medium uppercase text-White transition hover:bg-Primary-dark active:scale-[0.98] sm:h-12 sm:px-6 sm:text-base"
              : "inline-flex h-10 w-full items-center justify-center rounded-full border-[1.5px] border-Primary px-4 text-xs font-medium uppercase text-Primary transition hover:bg-Primary hover:text-white active:scale-[0.98] sm:h-12 sm:px-6 sm:text-base"
        }
      >
        Varyant Seç
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        addItem({ slug: product.slug, name: product.name, price: product.price, quantity: 1, image: product.image });
        trackAddToCart({ id: product.slug, name: product.name, price: product.price, quantity: 1 });
        setAdded(true);
        window.setTimeout(() => setAdded(false), 1600);
      }}
      className={
        variant === "white"
          ? "inline-flex h-10 items-center justify-center rounded-full bg-white px-4 text-xs font-medium uppercase text-Primary transition hover:bg-Background-light active:scale-[0.98] sm:h-12 sm:px-6 sm:text-base"
          : variant === "fill"
            ? "inline-flex h-10 w-full items-center justify-center rounded-full bg-Primary px-4 text-xs font-medium uppercase text-White transition hover:bg-Primary-dark active:scale-[0.98] sm:h-12 sm:px-6 sm:text-base"
            : "inline-flex h-10 w-full items-center justify-center rounded-full border-[1.5px] border-Primary px-4 text-xs font-medium uppercase text-Primary transition hover:bg-Primary hover:text-white active:scale-[0.98] sm:h-12 sm:px-6 sm:text-base"
      }
    >
      {added ? "Sepete Eklendi" : "Sepete Ekle"}
    </button>
  );
}

/** Varyantlı ürün satın alma kutusu (PDP) — Renk 01-20 hap seçimi + sepete ekle. */
export function VariantBuyBox({ product }: { product: Product }) {
  const variants = getVariants(product.slug);
  const { addItem } = useCart();
  const [selected, setSelected] = useState(variants[0] ?? "");
  const [added, setAdded] = useState(false);
  if (!product.inStock) {
    return (
      <span
        aria-disabled="true"
        className="inline-flex h-10 w-full cursor-not-allowed items-center justify-center rounded-full bg-Background-light px-4 text-xs font-medium uppercase text-500 outline outline-1 outline-Background-dark sm:h-12 sm:px-6 sm:text-base"
      >
        Stokta Yok
      </span>
    );
  }
  return (
    <div id="variant-selector" className="flex w-full flex-col gap-3 scroll-mt-32">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-Black">
          Renk: <span className="font-bold text-Primary">{selected}</span>
        </p>
        <p className="text-xs text-500">{variants.length} renk</p>
      </div>
      <div role="radiogroup" aria-label="Renk seç" className="grid max-h-44 grid-cols-4 gap-1.5 overflow-y-auto pr-0.5 sm:grid-cols-5">
        {variants.map((v) => {
          const active = v === selected;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setSelected(v)}
              className={`flex min-h-[44px] items-center justify-center rounded-xl px-2 py-2 text-xs font-semibold transition active:scale-95 sm:text-[13px] ${
                active
                  ? "bg-Primary text-White shadow"
                  : "bg-White text-800 outline outline-1 outline-Background-dark hover:border-Primary hover:text-Primary"
              }`}
            >
              {v}
            </button>
          );
        })}
      </div>
      <button
        id="main-add-to-cart-btn"
        type="button"
        onClick={() => {
          addItem({ slug: product.slug, name: product.name, price: product.price, quantity: 1, image: product.image, variant: selected });
          trackAddToCart({ id: product.slug, name: `${product.name} ${selected}`, price: product.price, quantity: 1 });
          setAdded(true);
          window.setTimeout(() => setAdded(false), 1600);
        }}
        className="inline-flex h-10 w-full items-center justify-center rounded-full bg-Primary px-4 text-xs font-medium uppercase text-White transition hover:bg-Primary-dark active:scale-[0.98] sm:h-12 sm:px-6 sm:text-base"
      >
        {added ? "Sepete Eklendi ✓" : `Sepete Ekle${selected ? ` — ${selected}` : ""}`}
      </button>
    </div>
  );
}

function CarouselDots({ total, index, onPick, label }: { total: number; index: number; onPick: (i: number) => void; label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 pt-10" role="tablist" aria-label={label}>
      {Array.from({ length: total }).map((_, i) => (
        <button
          key={i}
          role="tab"
          aria-selected={i === index}
          aria-label={`${label} ${i + 1}`}
          onClick={() => onPick(i)}
          className="flex min-h-[28px] min-w-[28px] items-center justify-center"
        >
          <span className={`relative block h-3 overflow-hidden rounded-full transition ${i === index ? "w-20 bg-Background-dark" : "w-3 bg-Background-dark/60 hover:bg-Background-dark"}`} aria-hidden="true">
            {i === index && <span aria-hidden="true" className="absolute left-0 top-0 h-full w-1/3 rounded-full bg-Tertiary" />}
          </span>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1. Hero — Makyaj çantası hediye slider                               */
/* ------------------------------------------------------------------ */

const HERO_SLIDES = [
  {
    title: "2500 TL Alışverişinize Makyaj Çantası Hediye",
    desc: "1500 TL ÜZERİ SİPARİŞLERDE ÜCRETSİZ KARGO!",
    cta: "Daha Fazlasını Keşfet",
    href: "/kampanyalar",
    src: "/images/hero/hero-makyaj-cantasi.png",
    fallback: unsplash.salon(1600, 900),
    alt: "Makyaj çantası hediyeli kampanya görseli",
  },
  {
    title: "Tüm Sitede %20 İndirim + 1500 TL Üzeri Kargo Bedava",
    desc: "ÇOK SATAN TIRNAK ÜRÜNLERİNDE SEZON FIRSATI",
    cta: "Hemen Alışverişe Başla",
    href: "/urunler",
    src: "/images/hero/4al3ode.jpg",
    fallback: unsplash.manicure(1600, 900),
    alt: "Sezon indirimi kampanya görseli",
  },
];

export function FigmaHero() {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    timer.current = window.setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, []);

  const go = (dir: 1 | -1) => {
    setIndex((i) => (i + dir + HERO_SLIDES.length) % HERO_SLIDES.length);
    if (timer.current) {
      window.clearInterval(timer.current);
      timer.current = window.setInterval(() => setIndex((v) => (v + 1) % HERO_SLIDES.length), 6000);
    }
  };

  return (
    <section aria-label="Öne çıkan kampanyalar" className="relative flex min-h-[480px] flex-col overflow-hidden bg-zinc-900 md:h-[560px]">
      {HERO_SLIDES.map((s, i) => (
        <div key={s.title} className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "pointer-events-none opacity-0"}`} aria-hidden={i !== index}>
          <SmartImage src={s.src} fallback={s.fallback} alt={i === index ? s.alt : ""} fill priority={i === 0} className="object-cover" sizes="100vw" />
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
        </div>
      ))}

      <div className="relative mx-auto flex w-full max-w-[1440px] flex-1 items-center px-4 py-14 md:px-12">
        <div key={index} className="flex w-full max-w-[529px] flex-col items-start justify-between gap-8 rounded-[32px] bg-gradient-to-br from-white/50 to-white/20 p-6 backdrop-blur-[10px] md:p-8">
          <div>
            <h1 className="text-3xl font-bold leading-[1.25] text-white drop-shadow md:text-4xl md:leading-[56px]">
              {HERO_SLIDES[index].title}
            </h1>
            <p className="mt-2 text-sm font-normal leading-6 tracking-wide text-white/95 drop-shadow md:text-base">
              {HERO_SLIDES[index].desc}
            </p>
          </div>
          <Link
            href={HERO_SLIDES[index].href}
            className="inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-3 text-base font-medium uppercase text-Primary transition hover:bg-Background-light active:scale-[0.98] sm:w-auto"
          >
            {HERO_SLIDES[index].cta}
          </Link>
        </div>
      </div>

      <button type="button" onClick={() => go(-1)} aria-label="Önceki kampanya" className="absolute left-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/50 bg-white/20 text-white backdrop-blur-[10px] transition hover:bg-white/35 md:inline-flex">
        <ChevronLeftIcon />
      </button>
      <button type="button" onClick={() => go(1)} aria-label="Sonraki kampanya" className="absolute right-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/50 bg-white/20 text-white backdrop-blur-[10px] transition hover:bg-white/35 md:inline-flex">
        <ChevronRightIcon />
      </button>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-1">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.href}
            onClick={() => setIndex(i)}
            aria-label={`Kampanya ${i + 1}`}
            className="flex min-h-[28px] min-w-[28px] items-center justify-center"
          >
            <span className={`h-2.5 rounded-full transition ${i === index ? "w-8 bg-white" : "w-2.5 bg-white/50 hover:bg-white/80"}`} aria-hidden="true" />
          </button>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 2. Senin Tarzın Senin Tırnakların — sekmeli ürün vitrini             */
/* ------------------------------------------------------------------ */

type FigmaTabId = "cok-satan" | "takma" | "jel" | "bakim" | "aksesuar";

const FIGMA_TABS: { id: FigmaTabId; label: string }[] = [
  { id: "cok-satan", label: "Çok Satanlar" },
  { id: "takma", label: "Takma Tırnak" },
  { id: "jel", label: "Jel Tırnak" },
  { id: "bakim", label: "Bakım Onarım" },
  { id: "aksesuar", label: "Aksesuarlar" },
];

function productsForTab(all: Product[], tab: FigmaTabId): Product[] {
  const pick = (pred: (p: Product) => boolean): Product[] => {
    const matched = all.filter(pred);
    if (matched.length >= 4) return matched.slice(0, 4);
    const rest = all.filter((p) => !matched.includes(p));
    return [...matched, ...rest].slice(0, 4);
  };
  switch (tab) {
    case "cok-satan":
      return pick((p) => p.badge === "cok-satan");
    case "takma":
      return pick((p) => p.category === "Takma Tırnak" || p.category === "Protez Tırnak");
    case "jel":
      return pick((p) => p.category === "Jel Tırnak" || p.category === "Tırnak Bakımı");
    case "bakim":
      return pick((p) => p.category === "Bakım Onarım" || p.category === "Cilt Bakımı");
    case "aksesuar":
      return pick((p) => p.category === "Makyaj" || p.category === "Aksesuar");
  }
}

export function FigmaProductCard({ product, priority }: { product: Product; priority?: boolean }) {
  const gallery = getProductImages(product);
  const hoverSrc = gallery[1];
  return (
    <article className="group relative box-border flex h-full min-h-[340px] w-full flex-1 isolate flex-col items-start justify-between gap-2 overflow-hidden rounded-[24px] border border-[#F3E8FA] bg-[#FBF6FF] p-2.5 transition hover:-translate-y-1 hover:shadow-xl sm:min-h-[420px] sm:p-4 md:min-h-[603px] md:gap-3">
      <div className="absolute right-2.5 top-2.5 z-10 sm:right-4 sm:top-4">
        <FigmaHeart product={product} />
      </div>
      <Link href={`/urunler/${product.slug}`} className="relative flex w-full flex-1 items-center justify-center" aria-label={product.name}>
        <SmartImage
          src={product.image}
          fallback={fallbackFor(product.slug)}
          alt={product.name}
          width={640}
          height={640}
          priority={priority}
          className="h-auto w-full max-w-[320px] rounded-2xl object-cover"
          sizes="(max-width: 640px) 45vw, (max-width: 1280px) 30vw, 20vw"
        />
        {hoverSrc && (
          <SmartImage
            src={hoverSrc}
            fallback={fallbackFor(product.slug)}
            alt=""
            aria-hidden="true"
            width={640}
            height={640}
            className="absolute inset-0 m-auto h-auto w-full max-w-[320px] rounded-2xl object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            sizes="(max-width: 640px) 45vw, (max-width: 1280px) 30vw, 20vw"
          />
        )}
      </Link>
      <div className="flex w-full flex-col items-start gap-2 rounded-2xl bg-white p-3 sm:gap-4 sm:p-5 md:p-6">
        {TIKTOK_FAVORITES.has(product.slug) && (
          <span className="inline-flex h-5 items-center rounded-lg bg-Tertiary px-2 text-xs font-medium uppercase leading-4 text-white sm:h-6 sm:text-sm">
            Tiktok Favorisi
          </span>
        )}
        <div className="flex w-full flex-col items-start gap-1">
          <FigmaStars slug={product.slug} />
          <p className="w-full text-[10px] font-semibold uppercase tracking-wider text-Primary sm:text-xs">{product.category}</p>
          <h3 className="line-clamp-2 w-full text-sm font-medium leading-5 text-zinc-900 sm:text-lg sm:leading-7">
            <Link href={`/urunler/${product.slug}`} className="transition hover:text-Primary">
              {product.name}
            </Link>
          </h3>
          <p className="line-clamp-2 w-full text-xs font-normal leading-4 text-zinc-500 sm:text-base sm:leading-6">{product.description}</p>
          <p className="mt-1 flex w-full flex-wrap items-end gap-2">
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-xs font-bold leading-5 text-zinc-500 line-through sm:text-sm sm:leading-6">{formatTL(product.originalPrice)}</span>
            )}
            <span className="text-base font-bold leading-6 text-Primary sm:text-lg sm:leading-7">{product.price > 0 ? formatTL(product.price) : "Çok yakında"}</span>
          </p>
        </div>
        <FigmaAddButton product={product} />
      </div>
    </article>
  );
}

export function FigmaProductTabs({ products }: { products: Product[] }) {
  const [tab, setTab] = useState<FigmaTabId>("cok-satan");
  const list = productsForTab(products, tab);

  return (
    <section aria-label="Tırnak ürünleri vitrini" className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-12 md:py-14">
      <div className="flex flex-col items-center gap-2 pb-6 text-center">
        <h2 className="text-3xl font-semibold leading-tight tracking-tight text-zinc-900 md:text-4xl md:leading-[56px]">
          Senin <AccentWord>Tarzın</AccentWord> Senin <AccentWord>Tırnakların</AccentWord>
        </h2>
        <p className="text-base font-normal leading-5 text-zinc-500">Kusursuz Tırnaklar için Favorini Seç:</p>
      </div>

      <div className="-mx-4 flex items-center gap-3 overflow-x-auto px-4 py-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:flex-wrap md:justify-center md:overflow-visible md:px-0" role="tablist" aria-label="Ürün kategorileri">
        {FIGMA_TABS.map((t) => {
          const selected = tab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={selected}
              onClick={() => setTab(t.id)}
              className={`shrink-0 snap-start rounded-lg px-5 py-2.5 text-sm font-medium uppercase leading-5 transition active:scale-[0.98] ${selected
                ? "bg-Primary text-white shadow"
                : "bg-white text-Primary outline outline-[1.5px] outline-Primary hover:bg-Background-light"
                }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div key={tab} role="tabpanel" className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4">
        {list.map((p, i) => (
          <FigmaProductCard key={p.slug} product={p} priority={i === 0} />
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 3. Video bant — Tarzını Öne Çıkar                                    */
/* ------------------------------------------------------------------ */

export function FigmaVideoBanner() {
  return (
    <section aria-label="Marka tanıtım videosu" className="relative flex min-h-[480px] flex-col overflow-hidden bg-zinc-900 md:h-[560px]">
      <SmartImage
        src="/images/hero/hero-isilti.png"
        fallback={unsplash.portrait(1600, 900)}
        alt="ONOMAC dokunuşuyla ışıltılı makyaj yapan model"
        fill
        className="object-cover"
        sizes="100vw"
      />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-l from-black/55 via-black/20 to-transparent" />

      <div className="relative mx-auto flex w-full max-w-[1440px] flex-1 items-center justify-end px-4 py-14 md:px-12">
        <div className="flex w-full max-w-[529px] flex-col items-start justify-between gap-8 rounded-[32px] p-6 backdrop-blur-[10px] md:p-8">
          <div>
            <h2 className="text-3xl font-bold leading-[1.25] text-white drop-shadow md:text-4xl md:leading-[56px]">
              Tarzını Öne Çıkar,
              <br />
              Işıltını Serbest Bırak
            </h2>
            <p className="mt-2 text-base font-normal leading-6 text-white/95 drop-shadow">
              ONOMAC dokunuşuyla her gün en göz alıcı halini keşfet.
            </p>
          </div>
          <Link
            href="/urunler"
            className="inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-3 text-base font-medium uppercase text-Primary transition hover:bg-Background-light active:scale-[0.98] sm:w-auto"
          >
            Daha Fazlasını Keşfet
          </Link>
        </div>
      </div>

      <Link
        href="/nasil-yapilir"
        aria-label="Tanıtım videosunu oynat"
        className="absolute bottom-8 left-4 inline-flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/50 bg-white/20 text-white backdrop-blur-md transition hover:scale-105 hover:bg-white/30 md:left-10 md:h-[60px] md:w-[60px]"
      >
        <PlayIcon />
      </Link>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 4. Sezonun En Sevilen Ürünü                                          */
/* ------------------------------------------------------------------ */

export function FigmaFeatured({ products }: { products: Product[] }) {
  const bySlug = (s: string) => products.find((p) => p.slug === s);
  const items = [
    bySlug("qnail-soft-gel-tips-oval-medium-550"),
    bySlug("qnail-builder-gel-50ml"),
    bySlug("uno-diamond-shield-top-coat"),
  ].filter((p): p is Product => Boolean(p));
  const [index, setIndex] = useState(0);
  const product = items[index] ?? products[0];
  if (!product) return null;

  return (
    <section aria-label="Sezonun en sevilen ürünü" className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-12 md:py-14">
      <div className="flex flex-col items-center gap-2 pb-6 text-center">
        <h2 className="text-3xl font-semibold leading-tight tracking-tight text-zinc-900 md:text-4xl md:leading-[56px]">
          Sezonun En Sevilen Ürünü <AccentWord>Tek Parça Çok İşlev</AccentWord>
        </h2>
      </div>

      <div className="relative mx-auto w-full max-w-[1100px]">
        <div key={product.slug} className="grid overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-black/5 md:grid-cols-2">
          <div className="relative min-h-[320px] bg-Background-light md:min-h-[650px]">
            <SmartImage
              src={product.image}
              fallback={fallbackFor(product.slug)}
              alt={product.name}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 40vw"
            />
          </div>
          <div className="flex flex-col justify-center gap-6 bg-Background-dark p-6 md:p-12">
            <div className="relative h-48 overflow-hidden rounded-3xl bg-Background-light md:h-80">
              <SmartImage
                src={product.image}
                fallback={fallbackFor(product.slug)}
                alt=""
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 30vw"
              />
            </div>
            <div className="flex flex-col items-start gap-4">
              <FigmaStars slug={product.slug} />
              <div>
                <h3 className="text-lg font-medium leading-7 text-zinc-900">{product.name}</h3>
                <p className="mt-0.5 text-base font-normal leading-6 text-zinc-500">{product.description}</p>
              </div>
              <p className="flex items-end gap-2">
                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="text-sm font-bold leading-6 text-zinc-500 line-through">{formatTL(product.originalPrice)}</span>
                )}
                <span className="text-lg font-bold leading-7 text-Primary">{formatTL(product.price)}</span>
              </p>
              <div className="w-44">
                <FigmaAddButton product={product} variant="white" />
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIndex((i) => (i - 1 + items.length) % items.length)}
          aria-label="Önceki ürün"
          className="absolute -left-3 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-Primary-dark/50 bg-white/20 text-Primary-dark opacity-100 backdrop-blur-[10px] transition hover:bg-white/40 md:inline-flex lg:-left-12"
        >
          <ChevronLeftIcon />
        </button>
        <button
          type="button"
          onClick={() => setIndex((i) => (i + 1) % items.length)}
          aria-label="Sonraki ürün"
          className="absolute -right-3 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-Primary-dark/50 bg-white/20 text-Primary-dark backdrop-blur-[10px] transition hover:bg-white/40 md:inline-flex lg:-right-12"
        >
          <ChevronRightIcon />
        </button>
      </div>

      <CarouselDots total={items.length} index={index} onPick={setIndex} label="Öne çıkan ürün" />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 5. %20 marquee + koleksiyon kartları                                 */
/* ------------------------------------------------------------------ */

const COLLECTIONS = [
  {
    title: "UNO Color Gel Seti",
    cta: "Uno Color Gel Renklerini Keşfet",
    href: "/urunler?kategori=jel-tirnak",
    src: "/images/collections/uno-color-gel.png",
    fallback: unsplash.polishBottle(800, 1000),
    alt: "UNO Color Gel seti ürün görseli",
  },
  {
    title: "QNAİL PROFESSİONAL Holografik Krom Tırnak Tozu Seti",
    cta: "Krom Tırnak Tozu Renklerini Keşfet",
    href: "/urunler?kategori=tirnak-bakimi",
    src: "/images/collections/holografik-krom.png",
    fallback: unsplash.palette(800, 1000),
    alt: "Holografik krom tırnak tozu seti görseli",
  },
  {
    title: "QNAİL PROFESSİONAL Soft Gel Tips Setleri",
    cta: "Soft Gel Tips Setlerini Keşfet",
    href: "/urunler?kategori=takma-tirnak",
    src: "/images/collections/soft-gel-tips.png",
    fallback: unsplash.nails(800, 1000),
    alt: "Soft gel tips setleri görseli",
  },
];

export function FigmaPromo() {
  const marquee = Array.from({ length: 4 });
  return (
    <section aria-label="Sezon indirimi ve koleksiyonlar" className="pt-10">
      <div className="overflow-hidden bg-Primary py-5" aria-label="Tüm sitede yüzde 20 indirim">
        <div className="flex w-max animate-marquee items-center gap-24 pr-24">
          {[0, 1].map((half) => (
            <div key={half} className="flex items-center gap-24" aria-hidden={half === 1}>
              {marquee.map((_, i) => (
                <p key={i} className="whitespace-nowrap text-2xl leading-10 text-white md:text-4xl">
                  <span className="font-normal">TÜM SİTEDE </span>
                  <span className="font-bold">%20 İNDİRİM</span>
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
        {COLLECTIONS.map((c) => (
          <article key={c.title} className="relative flex h-[480px] items-end justify-center overflow-hidden bg-rose-200 sm:h-[520px] md:h-[600px]">
            <SmartImage src={c.src} fallback={c.fallback} alt={c.alt} fill className="object-cover" sizes="(max-width: 768px) 100vw, 33vw" />
            <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-4 p-6 md:px-8 md:pb-10 md:pt-8">
              <h3 className="text-xl font-semibold leading-7 text-white drop-shadow md:text-2xl">{c.title}</h3>
              <Link
                href={c.href}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-base font-medium uppercase text-Primary transition hover:bg-Background-light active:scale-[0.98]"
              >
                {c.cta}
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section >
  );
}

/* ------------------------------------------------------------------ */
/* 6. Qnail Ürünlerini Deneyimle — reels                                */
/* ------------------------------------------------------------------ */

const REELS = [
  { src: "/images/reels/reel-1.png", fallback: unsplash.nails(600, 900), alt: "Builder gel uygulaması videosu" },
  { src: "/images/reels/reel-2.png", fallback: unsplash.polish(600, 900), alt: "Soft gel tips uygulaması videosu" },
  { src: "/images/reels/reel-3.png", fallback: unsplash.manicure(600, 900), alt: "Diamond top coat parlaklık videosu" },
  { src: "/images/reels/reel-4.png", fallback: unsplash.cosmetics(600, 900), alt: "Nail prep hazırlık videosu" },
  { src: "/images/reels/reel-5.png", fallback: unsplash.portrait(600, 900), alt: "Krom toz uygulaması videosu" },
];

export function FigmaReels({ products }: { products: Product[] }) {
  const scroller = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const card =
    products.find((p) => p.slug === "uno-diamond-shield-top-coat") ?? products[0];

  const scroll = (dir: 1 | -1) => {
    scroller.current?.scrollBy({ left: dir * 300, behavior: "smooth" });
  };

  if (!card) return null;

  return (
    <section aria-label="Ürün deneyim videoları" className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-12 md:py-14">
      <div className="flex flex-col items-center gap-6 pb-6">
        <h2 className="text-center text-3xl font-semibold leading-tight tracking-tight text-zinc-900 md:text-4xl md:leading-[56px]">
          Qnail Ürünlerini <AccentWord>Deneyimle</AccentWord>
        </h2>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => scroll(-1)}
            aria-label="Önceki videolar"
            className="flex h-12 w-12 items-center justify-center rounded-full border-[1.5px] border-Primary-dark/50 bg-white/20 text-Primary-dark backdrop-blur-[10px] transition hover:bg-white/40"
          >
            <ChevronLeftIcon />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            aria-label="Sonraki videolar"
            className="flex h-12 w-12 items-center justify-center rounded-full border-[1.5px] border-Primary-dark/50 bg-white/20 text-Primary-dark backdrop-blur-[10px] transition hover:bg-white/40"
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>

      <div ref={scroller} className="flex snap-x snap-mandatory items-center gap-6 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {REELS.map((r, i) => (
          <article
            key={r.src}
            className={`relative w-64 shrink-0 snap-center overflow-hidden rounded-2xl ${i === 2 ? "h-[560px] md:h-[700px] md:w-80" : "h-[480px] md:h-[573px]"}`}
          >
            <SmartImage src={r.src} fallback={r.fallback} alt={r.alt} fill className="object-cover" sizes="300px" />
            <div aria-hidden="true" className="absolute inset-0 bg-black/20" />
            {i === 2 && (
              <span className="absolute left-1/2 top-[206px] inline-flex -translate-x-1/2 items-center justify-center rounded-full border-2 border-white/50 bg-white/20 p-3.5 text-white backdrop-blur-md">
                <PlayIcon />
              </span>
            )}
            <div className={`absolute inset-x-0 bottom-0 ${i === 2 ? "bg-Background-light" : "bg-white"} p-3 outline outline-1 outline-zinc-200 ${i === 2 ? "rounded-[19.55px] p-3.5" : "rounded-2xl"}`}>
              <div className="flex items-start gap-3">
                <SmartImage
                  src={card.image}
                  fallback={fallbackFor(card.slug)}
                  alt=""
                  width={i === 2 ? 78 : 64}
                  height={i === 2 ? 78 : 64}
                  className="h-16 w-16 rounded-lg object-cover"
                  sizes="80px"
                />
                <div className="flex min-w-0 flex-1 flex-col items-start gap-px">
                  <p className={`font-medium text-zinc-900 ${i === 2 ? "text-lg leading-5" : "text-sm leading-4"}`}>QNAİL PROFESSİONAL<br />Uno Diamond</p>
                  <p className={`font-bold text-Primary ${i === 2 ? "text-xl leading-8" : "text-lg leading-7"}`}>₺800,00</p>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      <CarouselDots total={5} index={Math.min(page, 4)} onPick={setPage} label="Video sayfası" />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 7. Sosyal — Instagram / Facebook / TikTok                            */
/* ------------------------------------------------------------------ */

const SOCIALS = [
  {
    name: "Instagram",
    handle: "QNAIL BUILDER GEL",
    href: "https://www.instagram.com/onomackozmetik",
    src: "/images/social/instagram.png",
    fallback: unsplash.nails(800, 1000),
    alt: "Instagram'da Qnail builder gel gönderisi",
  },
  {
    name: "Facebook",
    handle: "UNO COLOR GEL",
    href: "https://www.facebook.com/profile.php?id=61594637455652",
    src: "/images/social/facebook.png",
    fallback: unsplash.polish(800, 1000),
    alt: "Facebook'ta Uno color gel gönderisi",
  },
  {
    name: "Tiktok",
    handle: "ONOMAC",
    href: "https://www.tiktok.com/@onomackozmetik",
    src: "/images/social/tiktok.png",
    fallback: unsplash.makeup(800, 1000),
    alt: "TikTok'ta Onomac videosu",
  },
];

export function FigmaSocial() {
  return (
    <section aria-label="Sosyal medya hesaplarımız" className="grid grid-cols-1 gap-0.5 sm:grid-cols-3">
      {SOCIALS.map((s) => (
        <article key={s.name} className="relative flex h-[420px] flex-col items-center justify-end overflow-hidden md:h-[560px] lg:h-[855px]">
          <SmartImage src={s.src} fallback={s.fallback} alt={s.alt} fill className="object-cover" sizes="(max-width: 768px) 100vw, 33vw" />
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/45" />
          <a
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute left-6 top-6 inline-flex items-center justify-center gap-2 rounded-full border-[1.5px] border-white/50 bg-white/20 px-6 py-3 text-base font-medium uppercase leading-6 text-white backdrop-blur-[10px] transition hover:bg-white/35 md:left-8 md:top-8"
          >
            {s.name}
            <ChevronRightIcon className="h-5 w-5" />
          </a>

        </article>
      ))}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 8. Güven şeridi — sayfa altı (layout footer'ının üstünde)            */
/* ------------------------------------------------------------------ */

const TRUST = [
  { icon: <TruckIcon />, text: "1500 TL Üzerine Kargo Bedava" },
  { icon: <ShieldIcon />, text: "Güvenli Alışveriş & Kolay İade" },
  { icon: <GiftIcon />, text: "Arkadaşını Davet Et, Kazan" },
];

export function FigmaTrust() {
  return (
    <section aria-label="Alışveriş avantajları" className="bg-Primary">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-6 px-8 py-10 sm:flex-row sm:justify-center sm:gap-0 md:h-36 md:py-0">
        {TRUST.map((t, i) => (
          <Fragment key={t.text}>
            {i > 0 && (
              <span aria-hidden="true" className="h-px w-24 shrink-0 bg-Primary-dark sm:h-20 sm:w-px md:h-24" />
            )}
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-white sm:px-8">
              <span aria-hidden="true" className="flex h-16 w-16 items-center justify-center md:h-20 md:w-20">
                {t.icon}
              </span>
              <p className="text-center text-base font-normal leading-6">{t.text}</p>
            </div>
          </Fragment>
        ))}
      </div>
    </section>
  );
}
